import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Briefcase, MapPin, ChevronDown, Trash2, FileText, FileDown, MessageCircleQuestion, Check, Mail, ClipboardList, X, Eye, Images, Package } from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail, fmtDateUS, fmtTsUS } from "@/lib/api";

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";
const selectCls = `${inputCls} appearance-none`;
const money = (n) => `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const money2 = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTs = fmtTsUS;

const JobDetail = ({ job, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [pctEdits, setPctEdits] = useState({});   // per-item completion edits
  const [billPcts, setBillPcts] = useState({});   // per-item "bill up to" % edits
  const [invAmount, setInvAmount] = useState("");
  const [invNote, setInvNote] = useState("");
  const [question, setQuestion] = useState("");
  const [receiptSel, setReceiptSel] = useState(null); // null = all pending client-charge receipts selected
  const [viewReceipt, setViewReceipt] = useState(null); // expense object shown in the receipt lightbox
  const [extraForm, setExtraForm] = useState({ title: "", quantity: "", unit: "SQ", labor: "", material: "", note: "" });
  const [extraFile, setExtraFile] = useState(null);
  const [extraBusy, setExtraBusy] = useState(false);

  const createExtra = async () => {
    if (!extraForm.title.trim()) return toast.error("Give the extra work a title");
    const qty = parseFloat(extraForm.quantity);
    if (!qty || qty <= 0) return toast.error("Enter the quantity (measurement)");
    const labor = parseFloat(extraForm.labor) || 0;
    const material = parseFloat(extraForm.material) || 0;
    if (labor + material <= 0) return toast.error("Enter a labor and/or material price");
    setExtraBusy(true);
    try {
      const { data: created } = await api.post(`/admin/jobs/${job.id}/extras`, {
        title: extraForm.title.trim(), unit: extraForm.unit, quantity: qty,
        labor_rate: labor, material_rate: material, note: extraForm.note.trim(),
      });
      if (extraFile) {
        const fd = new FormData();
        fd.append("file", extraFile);
        await api.post(`/admin/jobs/${job.id}/extras/${created.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Extra charge posted — the client can approve or deny it in their portal");
      setExtraForm({ title: "", quantity: "", unit: "SQ", labor: "", material: "", note: "" });
      setExtraFile(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setExtraBusy(false);
    }
  };
  const [emailClient, setEmailClient] = useState(true);
  const [matAnswers, setMatAnswers] = useState({});

  const answerMaterial = async (o) => {
    setBusy(true);
    try {
      await api.post(`/admin/jobs/${job.id}/material-orders/${o.id}/answer`, {
        status: "ordered", eta: matAnswers[o.id]?.eta || "", note: matAnswers[o.id]?.note || "",
      });
      toast.success(`"${o.item}" marked ordered`);
      setMatAnswers({ ...matAnswers, [o.id]: {} });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const [foremen, setForemen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("overview");
  const [delPin, setDelPin] = useState(null); // null = delete confirm closed

  const load = useCallback(() => {
    api.get(`/admin/jobs/${job.id}`).then(({ data }) => { setDetail(data); }).catch(() => {});
    api.get("/admin/employees").then(({ data }) => setForemen(data.filter((e) => e.pin))).catch(() => {});
  }, [job.id]);
  useEffect(() => { load(); }, [load]);

  const saveItemPct = async (itemId) => {
    const pct = parseFloat(pctEdits[itemId]);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) return toast.error("Enter a percent between 0 and 100");
    setBusy(true);
    try {
      await api.post(`/admin/jobs/${job.id}/items/${itemId}/progress`, { pct });
      toast.success("Progress updated");
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // itemized billing: auto-fill the invoice amount from the "bill up to" table
  const billSuggestion = (map) => (detail?.progress?.items || []).reduce((s, it) => {
    const to = parseFloat(map[it.id]);
    const billed = it.billed_pct || 0;
    if (!Number.isFinite(to) || to <= billed) return s;
    const capped = Math.min(to, it.pct || 0);
    return capped > billed ? s + (it.approved_price * (capped - billed)) / 100 : s;
  }, 0);

  const setBill = (id, val) => {
    const next = { ...billPcts, [id]: val };
    setBillPcts(next);
    const s = billSuggestion(next);
    setInvAmount(s > 0 ? String(Math.round(s * 100) / 100) : "");
  };

  const setReportVisibility = async (rep, visible) => {
    try {
      await api.post(`/admin/jobs/${job.id}/reports/${rep.id}/visibility`, { visible });
      toast.success(visible ? "Report now shows on the customer's page" : "Report hidden from the customer");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/jobs/${job.id}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const suggestAmount = () => {
    if (!detail) return;
    const earned = (detail.total_price * (detail.completion_pct || 0)) / 100;
    const invoiced = (detail.invoices || []).reduce((s, i) => s + i.amount, 0);
    setInvAmount(String(Math.max(0, Math.round(earned - invoiced))));
  };

  const createInvoice = async () => {
    if (!invAmount || parseFloat(invAmount) <= 0) return toast.error("Enter an amount");
    setBusy(true);
    try {
      const pending = (detail.expenses || []).filter((e) => !e.invoiced);
      const billablePending = pending.filter((e) => e.billable);
      const { data } = await api.post(`/admin/jobs/${job.id}/invoices`, {
        amount: parseFloat(invAmount), note: invNote, origin: window.location.origin,
        expense_ids: receiptSel ?? billablePending.map((e) => e.id),
        email_client: emailClient,
        item_lines: Object.entries(billPcts)
          .map(([item_id, p]) => ({ item_id, pct: parseFloat(p) }))
          .filter((l) => Number.isFinite(l.pct) && l.pct > 0),
      });
      toast.success(data.emailed ? `Invoice #${data.number} created and emailed to the client` : `Invoice #${data.number} created — the client sees it in their portal`);
      setInvAmount("");
      setInvNote("");
      setReceiptSel(null);
      setBillPcts({});
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleReceipt = (id) => {
    const cur = receiptSel ?? (detail?.expenses || []).filter((e) => !e.invoiced && e.billable).map((e) => e.id);
    setReceiptSel(cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const markPaid = async (inv) => {
    try {
      await api.patch(`/admin/jobs/${job.id}/invoices/${inv.id}`, { status: "paid" });
      toast.success(`Invoice #${inv.number} marked paid`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadInvoicePdf = async (inv) => {
    try {
      const { data } = await api.get(`/admin/jobs/${job.id}/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${inv.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the invoice");
    }
  };

  const emailReport = async (rep) => {
    try {
      await api.post(`/admin/jobs/${job.id}/reports/${rep.id}/email`, { origin: window.location.origin });
      toast.success(`Report emailed to ${detail.client_email}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadReportPdf = async (rep) => {
    try {
      const { data } = await api.get(`/admin/jobs/${job.id}/reports/${rep.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `daily-report-${rep.report_date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the report PDF");
    }
  };

  const askQuestion = async () => {
    if (!question.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/jobs/${job.id}/clarifications`, { question });
      toast.success("Question posted — the client sees it in their portal");
      setQuestion("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!detail) return <p className="text-white/40 text-xs py-4 text-center" data-testid={`job-detail-loading-${job.id}`}>Loading project...</p>;

  const paid = (detail.invoices || []).filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0);

  return (
    <div className="border-t border-white/10 px-4 py-4 space-y-5 bg-white/[0.02]" data-testid={`job-detail-${job.id}`}>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="border border-white/10 rounded-lg bg-background/60 p-3">
          <p className="font-display text-xl text-primary" data-testid={`job-labor-hours-${job.id}`}>{detail.labor_hours}h</p>
          <p className="text-[9px] text-white/40 uppercase tracking-widest">Hours worked</p>
        </div>
        <div className="border border-white/10 rounded-lg bg-background/60 p-3">
          <p className="font-display text-xl text-primary" data-testid={`job-labor-cost-${job.id}`}>{money(detail.labor_cost)}</p>
          <p className="text-[9px] text-white/40 uppercase tracking-widest">Payroll cost</p>
        </div>
        <div className="border border-white/10 rounded-lg bg-background/60 p-3">
          <p className="font-display text-xl text-primary" data-testid={`job-paid-${job.id}`}>{money(paid)}</p>
          <p className="text-[9px] text-white/40 uppercase tracking-widest">Invoices paid</p>
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid={`job-tabs-${job.id}`}>
        {[
          ["overview", "Overview", null],
          ["progress", "Progress", (detail.items_progress || []).length],
          ["invoices", "Invoices", (detail.invoices || []).length],
          ["extras", "Extra Items", (detail.extras || []).length],
          ["materials", "Materials", (detail.material_orders || []).length],
          ["reports", "Reports", (detail.daily_reports || []).length],
          ["messages", "Messages", (detail.clarifications || []).length],
          ["activity", "Activity", null],
        ].map(([key, label, count]) => (
          <button key={key} onClick={() => setView(key)} data-testid={`job-tab-${key}-${job.id}`}
                  className={`shrink-0 flex items-center gap-1.5 px-3.5 py-2 text-[10px] font-bold uppercase tracking-widest rounded-lg border transition-colors duration-200 ${view === key ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/55 hover:border-primary/50 hover:text-primary"}`}>
            {label}
            {count > 0 && <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${view === key ? "bg-black/25" : "bg-white/10"}`}>{count}</span>}
            {key === "messages" && (detail.clarifications || []).some((c) => c.status === "open") && <span className="w-1.5 h-1.5 rounded-full bg-amber-300" data-testid={`job-tab-messages-open-${job.id}`} />}
          </button>
        ))}
      </div>

      {view === "overview" && (<>
      {detail.labor_by_employee.length > 0 && (
        <div className="space-y-1" data-testid={`job-labor-by-emp-${job.id}`}>
          {detail.labor_by_employee.map((l) => (
            <p key={l.name} className="text-[11px] text-white/55 flex justify-between"><span>{l.name}</span><span>{l.hours}h · {money(l.cost)}</span></p>
          ))}
        </div>
      )}

      {(detail.estimate_breakdown?.total_cost > 0 || detail.labor_cost > 0 || detail.expenses_total > 0) && (
        <div className="border border-primary/25 rounded-xl bg-primary/5 p-3.5 space-y-2" data-testid={`job-profit-${job.id}`}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-primary">Profit — only you see this</p>
          {detail.estimate_breakdown?.total_cost > 0 && (
            <div className="space-y-1 text-[11px] text-white/60">
              {detail.estimate_breakdown.lines ? (
                <>
                  <p className="flex justify-between"><span>Estimated — {detail.estimate_breakdown.days} days · {detail.estimate_breakdown.workers} workers · {detail.estimate_breakdown.hours_per_day} h/day · {detail.estimate_breakdown.miles_per_day} mi/day</span><span>{money(detail.estimate_breakdown.total_cost)}</span></p>
                  {detail.estimate_breakdown.lines.map((l) => (
                    <p key={l.id} className="flex justify-between text-white/45 pl-2" data-testid={`job-est-line-${job.id}-${l.id}`}><span>{l.name} — {l.quantity} × {money2(l.rate)}</span><span>{money(l.amount)}</span></p>
                  ))}
                </>
              ) : (
                <p className="flex justify-between"><span>Estimated cost — {detail.estimate_breakdown.days} days, {detail.estimate_breakdown.total_miles} mi driving, insurance, payroll, management</span><span>{money(detail.estimate_breakdown.total_cost)}</span></p>
              )}
              <p className="flex justify-between"><span>Expected profit</span><span className={detail.estimate_breakdown.expected_profit >= 0 ? "text-white/85 font-semibold" : "text-red-400 font-semibold"} data-testid={`job-expected-profit-${job.id}`}>{money(detail.estimate_breakdown.expected_profit)}</span></p>
            </div>
          )}
          <div className="space-y-1 text-[11px] text-white/60 border-t border-white/10 pt-2">
            <p className="flex justify-between"><span>Actual so far — payroll {money(detail.labor_cost)} + expenses {money(detail.expenses_total)}</span><span>{money(detail.estimate_breakdown?.actual_cost ?? 0)}</span></p>
            <p className="flex justify-between font-bold text-sm"><span>Actual profit so far</span><span className={(detail.estimate_breakdown?.actual_profit ?? 0) >= 0 ? "text-primary" : "text-red-400"} data-testid={`job-actual-profit-${job.id}`}>{money(detail.estimate_breakdown?.actual_profit ?? 0)}</span></p>
          </div>
        </div>
      )}

      {detail.expenses?.length > 0 && (
        <div className="space-y-2" data-testid={`job-expenses-${job.id}`}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">Crew expenses — receipts</p>
          {[...detail.expenses].reverse().map((e) => (
            <div key={e.id} className="flex items-center gap-3 border border-white/10 rounded-lg bg-background/60 px-3.5 py-2.5" data-testid={`expense-${e.id}`}>
              <a href={mediaUrl(e.receipt_path)} target="_blank" rel="noreferrer" className="shrink-0">
                <img src={mediaUrl(e.receipt_path)} alt="Receipt" loading="lazy" className="w-10 h-10 object-cover rounded border border-white/15" />
              </a>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">{money(e.amount)}{e.note ? <span className="text-white/50 font-normal"> · {e.note}</span> : null}</p>
                <p className="text-[11px] text-white/45">{e.by} · {fmtTs(e.ts)}</p>
              </div>
              <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${e.invoiced ? "bg-primary/15 text-primary" : e.billable ? "bg-sky-400/15 text-sky-300" : "bg-amber-400/15 text-amber-300"}`} data-testid={`expense-status-${e.id}`}>{e.invoiced ? "Invoiced" : e.billable ? "Charge client · next invoice" : "Pending"}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <button onClick={() => setView("progress")} data-testid={`job-progress-link-${job.id}`} className="flex-1 text-left border border-white/10 rounded-lg bg-background/60 px-3.5 py-2.5 hover:border-primary/50 transition-colors duration-200">
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/45 block mb-1">Completion — calculated from item progress</span>
          <span className="font-display text-xl text-primary" data-testid={`job-overall-pct-${job.id}`}>{detail.completion_pct}%</span>
          <span className="text-[10px] text-white/35 block">tap to update item by item</span>
        </button>
        <select value={detail.status} onChange={(e) => setStatus(e.target.value)} data-testid={`job-status-${job.id}`} className={`${selectCls} !w-auto`}>
          <option value="open" className="bg-[#252720]">Open</option>
          <option value="on_hold" className="bg-[#252720]">On hold</option>
          <option value="completed" className="bg-[#252720]">Completed</option>
        </select>
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="text-[10px] font-bold uppercase tracking-widest text-white/45 block mb-1.5">Foreman in charge — anyone with a PIN; this project shows on their /time page</label>
          <select
            value={detail.assigned_foreman_id || ""}
            onChange={async (e) => {
              try {
                await api.patch(`/admin/jobs/${job.id}`, { foreman_id: e.target.value });
                toast.success(e.target.value ? "Foreman assigned — they now see this project on their page" : "Foreman removed");
                load();
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail));
              }
            }}
            data-testid={`job-foreman-${job.id}`}
            className={selectCls}
          >
            <option value="" className="bg-[#252720]">No foreman assigned</option>
            {foremen.map((f) => <option key={f.id} value={f.id} className="bg-[#252720]">{f.name}{f.role === "foreman" ? "" : " (worker)"} — PIN {f.pin}</option>)}
          </select>
        </div>
      </div>

      {delPin === null ? (
      <button
        onClick={() => setDelPin("")}
        data-testid={`job-delete-${job.id}`}
        className="text-[10px] font-bold uppercase tracking-widest text-red-400/60 hover:text-red-300 transition-colors duration-200"
      >
        Delete project
      </button>
      ) : (
      <div className="border border-red-400/40 bg-red-400/5 rounded-lg p-3 space-y-2" data-testid={`job-delete-confirm-${job.id}`}>
        <p className="text-[11px] text-red-200/80 leading-relaxed">
          Delete "{job.title}"? Hours, invoices and answers are removed from view. Enter the manager PIN to confirm.
        </p>
        <div className="flex gap-2">
          <input value={delPin} onChange={(e) => setDelPin(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="Manager PIN" inputMode="numeric" type="password" autoFocus data-testid={`job-delete-pin-${job.id}`} className={inputCls} />
          <button
            onClick={async () => {
              try {
                await api.delete(`/admin/jobs/${job.id}`, { params: { pin: delPin } });
                toast.success("Project deleted");
                setDelPin(null);
                onChanged();
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete");
              }
            }}
            data-testid={`job-delete-go-${job.id}`}
            className="px-4 bg-red-500/90 hover:bg-red-500 text-white text-[11px] font-bold uppercase tracking-widest rounded-lg transition-colors duration-200"
          >
            Delete
          </button>
          <button onClick={() => setDelPin(null)} data-testid={`job-delete-cancel-${job.id}`} className="px-3 text-[11px] font-bold uppercase tracking-widest text-white/50 hover:text-white">
            Cancel
          </button>
        </div>
      </div>
      )}
      </>)}

      {view === "progress" && (
      <div className="space-y-3" data-testid={`job-progress-${job.id}`}>
        <div className="border border-primary/25 rounded-xl bg-primary/5 p-3.5">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-widest text-primary">Overall progress — weighted by approved price</p>
            <p className="font-display text-2xl text-primary" data-testid={`job-progress-overall-${job.id}`}>{detail.progress?.overall_pct ?? detail.completion_pct}%</p>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden mt-2">
            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${detail.progress?.overall_pct ?? detail.completion_pct}%` }} />
          </div>
          <p className="text-[10px] text-white/40 mt-2">All money figures below are <strong className="text-white/60">results to date</strong> — not final project profit.</p>
          <p className="text-[10px] text-white/45 mt-1" data-testid={`job-labor-summary-${job.id}`}>
            Labor so far: <strong className="text-white/70">{detail.labor_hours ?? 0}h across {detail.labor_days ?? 0} day{detail.labor_days === 1 ? "" : "s"}</strong> = {money2(detail.labor_cost ?? 0)} (payroll-style, after lunch/deductions) · receipts {money2(detail.expenses_total ?? 0)}
          </p>
          {(detail.progress?.unallocated_cost ?? 0) > 0 && (
            <p className="text-[10px] text-amber-300/80 mt-1" data-testid={`job-unallocated-${job.id}`}>{money2(detail.progress.unallocated_cost)} of hours/receipts not tagged to any item — counts toward the project but no single item.</p>
          )}
        </div>
        {(detail.progress?.categories || []).map((cat) => (
          <div key={cat.name} className="border border-white/10 rounded-xl bg-background/60 overflow-hidden" data-testid={`prog-cat-${job.id}-${cat.name}`}>
            <div className="px-3.5 py-3 border-b border-white/10">
              <div className="flex items-center justify-between gap-2">
                <p className="font-bold text-sm">{cat.name} <span className="text-white/35 font-normal text-[11px]">· {cat.item_count} item{cat.item_count === 1 ? "" : "s"}</span></p>
                <p className="font-display text-xl text-primary" data-testid={`prog-cat-pct-${job.id}-${cat.name}`}>{cat.pct}%</p>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mt-1.5">
                <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${cat.pct}%` }} />
              </div>
              <p className="text-[10px] text-white/45 mt-1.5">
                Approved {money2(cat.approved_price)} · done {money2(cat.completed_value)} · cost {money2(cat.actual_cost)} · diff <span className={cat.difference >= 0 ? "text-primary" : "text-red-400"}>{money2(cat.difference)}</span> · margin <span data-testid={`prog-cat-margin-${job.id}-${cat.name}`}>{cat.margin_pct === null ? "—" : `${cat.margin_pct}%`}</span>
              </p>
            </div>
            <div className="divide-y divide-white/5">
              {(detail.progress?.items || []).filter((i) => (i.category || "Other") === cat.name).map((it) => (
                <div key={it.id} className="px-3.5 py-2.5 space-y-1.5" data-testid={`prog-item-${it.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-white/85 min-w-0 truncate">{it.name}{it.source === "extra" && <span className="text-sky-300 text-[9px] font-bold uppercase tracking-widest"> · extra work</span>}</p>
                    <p className="text-[10px] text-white/40 shrink-0">{it.quantity} {it.unit} · {money2(it.approved_price)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" max="100" step="1"
                      value={pctEdits[it.id] ?? String(it.pct)}
                      onChange={(e) => setPctEdits({ ...pctEdits, [it.id]: e.target.value })}
                      data-testid={`prog-pct-input-${it.id}`}
                      className="w-20 bg-white/5 border border-white/15 rounded-lg px-2.5 py-2 text-sm text-white focus:outline-none focus:border-primary"
                    />
                    <span className="text-[10px] text-white/40">%</span>
                    <button onClick={() => saveItemPct(it.id)} disabled={busy} data-testid={`prog-pct-save-${it.id}`} className="border border-primary/60 text-primary text-[10px] font-bold uppercase tracking-widest px-3 py-2 rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors duration-200 disabled:opacity-50">Save</button>
                    {it.updated_by && <span className="text-[9px] text-white/30 ml-auto" data-testid={`prog-item-by-${it.id}`}>{it.updated_by} · {fmtDateUS(it.updated_at)}</span>}
                  </div>
                  <p className="text-[10px] text-white/45">
                    Done <span className="text-white/75" data-testid={`prog-item-completed-${it.id}`}>{money2(it.completed_value)}</span>
                    {" "}· cost <span className="text-white/75" data-testid={`prog-item-cost-${it.id}`}>{money2(it.actual_cost)}</span>
                    {" "}· diff <span className={it.difference >= 0 ? "text-primary" : "text-red-400"} data-testid={`prog-item-diff-${it.id}`}>{money2(it.difference)}</span>
                    {" "}· margin <span className="text-white/75" data-testid={`prog-item-margin-${it.id}`}>{it.margin_pct === null ? "—" : `${it.margin_pct}%`}</span>
                    {(it.billed_pct || 0) > 0 && <span className="text-white/30"> · billed {it.billed_pct}%</span>}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
        {(detail.progress?.categories || []).length === 0 && (
          <p className="text-[11px] text-white/35">No tracked items on this project — items appear here from the approved estimate and approved extra work.</p>
        )}
      </div>
      )}

      {view === "invoices" && (
      <div className="space-y-2.5" data-testid={`job-invoices-${job.id}`}>
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/45 flex items-center gap-2"><FileText size={12} className="text-primary" aria-hidden="true" /> Invoices — client sees these in their portal</p>
        {(detail.invoices || []).map((inv) => (
          <div key={inv.id} className="flex items-center gap-3 border border-white/10 rounded-lg bg-background/60 px-3.5 py-2.5" data-testid={`invoice-${inv.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Invoice #{inv.number} · {money(inv.amount)}</p>
              <p className="text-[11px] text-white/45">{fmtTs(inv.created_at)}{inv.note ? ` · ${inv.note}` : ""}</p>
            </div>
            <button onClick={() => downloadInvoicePdf(inv)} data-testid={`invoice-pdf-${inv.id}`} aria-label={`Download invoice ${inv.number}`} className="text-white/40 hover:text-primary p-1.5 transition-colors duration-200"><FileDown size={15} /></button>
            {inv.status === "paid"
              ? <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-300 bg-emerald-400/15 px-2.5 py-1 rounded-full">Paid</span>
              : <button onClick={() => markPaid(inv)} data-testid={`invoice-paid-${inv.id}`} className="text-[10px] font-bold uppercase tracking-widest border border-primary/50 text-primary px-2.5 py-1.5 rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors duration-200">Mark paid</button>}
          </div>
        ))}
        {(detail.progress?.items || []).length > 0 && (
          <div className="space-y-1.5 border border-white/10 rounded-lg p-2.5" data-testid={`invoice-items-${job.id}`}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">Bill by completed work — only the new % since the last invoice is charged (never past the completed %)</p>
            {detail.progress.items.map((it) => {
              const billed = it.billed_pct || 0;
              const to = parseFloat(billPcts[it.id]);
              const capped = Number.isFinite(to) ? Math.min(to, it.pct || 0) : billed;
              const lineAmt = capped > billed ? (it.approved_price * (capped - billed)) / 100 : 0;
              return (
                <div key={it.id} className="flex items-center gap-2 text-[11px] text-white/70" data-testid={`inv-line-${it.id}`}>
                  <span className="flex-1 min-w-0 truncate">{it.name} <span className="text-white/35">· {money2(it.approved_price)}</span></span>
                  <span className="text-white/40 shrink-0">done {it.pct}% · billed {billed}%</span>
                  <input
                    type="number" min="0" max="100" step="1" placeholder="to %"
                    value={billPcts[it.id] ?? ""}
                    onChange={(e) => setBill(it.id, e.target.value)}
                    data-testid={`inv-line-input-${it.id}`}
                    className="w-16 shrink-0 bg-white/5 border border-white/15 rounded-lg px-2 py-1.5 text-white focus:outline-none focus:border-primary"
                  />
                  <span className="w-16 text-right font-bold text-white/90 shrink-0" data-testid={`inv-line-amt-${it.id}`}>{lineAmt > 0 ? money2(lineAmt) : "—"}</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex gap-2">
          <input type="number" min="0" step="0.01" value={invAmount} onChange={(e) => setInvAmount(e.target.value)} placeholder="Amount $" data-testid={`invoice-amount-${job.id}`} className={inputCls} />
          <button onClick={suggestAmount} data-testid={`invoice-suggest-${job.id}`} className="shrink-0 border border-white/20 text-white/60 text-[10px] font-bold uppercase tracking-widest px-3 rounded-lg hover:border-primary hover:text-primary transition-colors duration-200" title={`${detail.completion_pct}% of ${money(detail.total_price)} minus already invoiced`}>
            Suggest {detail.completion_pct}%
          </button>
        </div>
        {(() => {
          const pending = (detail.expenses || []).filter((e) => !e.invoiced);
          const billablePending = pending.filter((e) => e.billable);
          const receiptIds = receiptSel ?? billablePending.map((e) => e.id);
          const receiptSum = pending.filter((e) => receiptIds.includes(e.id)).reduce((s, e) => s + e.amount, 0);
          return (
            <>
              {pending.length > 0 && (
                <div className="space-y-1.5 border border-sky-400/25 rounded-lg p-2.5" data-testid={`invoice-receipts-${job.id}`}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-sky-300/80">Receipts on file — tick the ones to add at cost to this invoice</p>
                  {pending.map((e) => (
                    <div key={e.id} className="flex items-center gap-2.5 text-[11px] text-white/70" data-testid={`invoice-receipt-${e.id}`}>
                      <input type="checkbox" checked={receiptIds.includes(e.id)} onChange={() => toggleReceipt(e.id)} aria-label={`Add receipt ${e.note || e.id}`} className="w-4 h-4 accent-primary cursor-pointer shrink-0" />
                      <span className="flex-1 min-w-0 truncate">{fmtTs(e.ts)} · {e.note || "receipt"} · by {e.by}{!e.billable && <span className="text-amber-300/80"> · internal</span>}</span>
                      <span className="font-bold text-white/90 shrink-0">{money2(e.amount)}</span>
                      {e.receipt_path && (
                        <button type="button" onClick={() => setViewReceipt(e)} aria-label={`View receipt photo ${e.note || e.id}`} data-testid={`receipt-view-${e.id}`} className="shrink-0 text-white/40 hover:text-primary p-1 transition-colors duration-200">
                          <Eye size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <p className="text-[10px] text-white/45" data-testid={`invoice-receipts-total-${job.id}`}>
                    {receiptIds.length} of {pending.length} picked ({money2(receiptSum)}) — invoice total will be {money2((parseFloat(invAmount) || 0) + receiptSum)}
                  </p>
                </div>
              )}
            </>
          );
        })()}
        <div className="flex gap-2">
          <input value={invNote} onChange={(e) => setInvNote(e.target.value)} placeholder="Note — e.g. 'Deposit' or 'Progress bill'" data-testid={`invoice-note-${job.id}`} className={inputCls} />
          <button onClick={createInvoice} disabled={busy} data-testid={`invoice-create-${job.id}`} className="shrink-0 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs px-5 rounded-lg disabled:opacity-50">Create</button>
        </div>
        {detail.client_email && (
          <label className="flex items-center gap-2.5 text-[11px] text-white/60 cursor-pointer" data-testid={`invoice-email-toggle-${job.id}`}>
            <input type="checkbox" checked={emailClient} onChange={(e) => setEmailClient(e.target.checked)} className="w-4 h-4 accent-primary" />
            Email this invoice to {detail.client_email}
          </label>
        )}
      </div>
      )}

      {view === "extras" && (
      <div className="space-y-2.5" data-testid={`job-extras-${job.id}`}>
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Extra items — additional work you charge the client for</p>
        <div className="border border-primary/25 bg-primary/5 rounded-lg p-3 space-y-2" data-testid={`extra-form-${job.id}`}>
          <input value={extraForm.title} onChange={(e) => setExtraForm({ ...extraForm, title: e.target.value })} placeholder="Title — e.g. 'Rotted sheathing replacement'" data-testid={`extra-title-${job.id}`} className={inputCls} />
          <div className="grid grid-cols-2 gap-2">
            <input type="number" min="0" step="0.01" value={extraForm.quantity} onChange={(e) => setExtraForm({ ...extraForm, quantity: e.target.value })} placeholder="Measurement (qty)" data-testid={`extra-qty-${job.id}`} className={inputCls} />
            <select value={extraForm.unit} onChange={(e) => setExtraForm({ ...extraForm, unit: e.target.value })} data-testid={`extra-unit-${job.id}`} className={selectCls}>
              <option value="SQ" className="bg-[#252720]">SQ (100 sq ft)</option>
              <option value="SQFT" className="bg-[#252720]">SQFT</option>
              <option value="LNFT" className="bg-[#252720]">LNFT</option>
              <option value="EACH" className="bg-[#252720]">Unit (each)</option>
              <option value="FLAT" className="bg-[#252720]">One price (flat)</option>
            </select>
            <input type="number" min="0" step="0.01" value={extraForm.labor} onChange={(e) => setExtraForm({ ...extraForm, labor: e.target.value })} placeholder="Labor price / unit" data-testid={`extra-labor-${job.id}`} className={inputCls} />
            <input type="number" min="0" step="0.01" value={extraForm.material} onChange={(e) => setExtraForm({ ...extraForm, material: e.target.value })} placeholder="Material price / unit" data-testid={`extra-material-${job.id}`} className={inputCls} />
          </div>
          <input value={extraForm.note} onChange={(e) => setExtraForm({ ...extraForm, note: e.target.value })} placeholder="Extra note for the client (optional)" data-testid={`extra-note-${job.id}`} className={inputCls} />
          <label className={`flex items-center justify-center gap-2 border border-dashed rounded-lg py-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-300 cursor-pointer ${extraFile ? "border-primary/60 text-primary" : "border-white/20 text-white/50 hover:border-primary hover:text-primary"}`} data-testid={`extra-photo-${job.id}`}>
            <Images size={13} aria-hidden="true" /> {extraFile ? `Photo picked: ${extraFile.name.slice(0, 20)}` : "Attach a photo (optional)"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { setExtraFile(e.target.files?.[0] || null); e.target.value = ""; }} />
          </label>
          <p className="text-[11px] text-white/50" data-testid={`extra-total-${job.id}`}>
            Total: <span className="text-primary font-bold">${(((parseFloat(extraForm.quantity) || 0) * ((parseFloat(extraForm.labor) || 0) + (parseFloat(extraForm.material) || 0)))).toFixed(2)}</span>
            <span className="text-white/35"> — qty × (labor + material), calculated automatically</span>
          </p>
          <button onClick={createExtra} disabled={extraBusy} data-testid={`extra-create-${job.id}`} className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-[11px] py-3 rounded-lg disabled:opacity-50">
            {extraBusy ? "Posting..." : "Create additional work — send to client"}
          </button>
        </div>
        {(detail.extras || []).length === 0 && <p className="text-[11px] text-white/35">No extra charges on this project yet.</p>}
        {(detail.extras || []).slice().reverse().map((e) => (
          <div key={e.id} className="border border-white/10 rounded-lg bg-background/60 px-3.5 py-2.5" data-testid={`extra-row-${e.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold truncate">
                {e.title}{" "}
                <span className={`text-[9px] font-bold uppercase tracking-widest ${e.status === "approved" ? "text-primary" : e.status === "denied" ? "text-red-300" : "text-amber-300"}`} data-testid={`extra-status-${e.id}`}>{e.status}</span>
              </p>
              <p className="text-sm font-bold text-primary shrink-0">{money2(e.amount)}</p>
            </div>
            <p className="text-[11px] text-white/45">{e.quantity} {e.unit} × {money2(e.rate)} ({money2(e.labor_rate)} labor + {money2(e.material_rate)} material){e.note ? ` · ${e.note}` : ""}</p>
            {e.photo_path && (
              <button type="button" onClick={() => setViewReceipt({ receipt_path: e.photo_path, note: e.title })} data-testid={`extra-photo-view-${e.id}`} className="mt-1.5">
                <img src={mediaUrl(e.photo_path)} alt={e.title} className="w-14 h-14 object-cover rounded-lg border border-white/10" />
              </button>
            )}
            {e.client_note && <p className="text-[11px] text-sky-300/80 mt-0.5">Client note: {e.client_note}</p>}
            {e.status === "approved" && <p className="text-[10px] text-white/35 mt-0.5">{e.invoiced ? "Added to an invoice" : "Approved — rides onto the next invoice and the project total"}</p>}
          </div>
        ))}
      </div>
      )}

      {view === "materials" && (
      <div className="space-y-2.5" data-testid={`job-materials-${job.id}`}>
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/45 flex items-center gap-2"><Package size={12} className="text-primary" aria-hidden="true" /> Material orders from the foreman{detail.supply_mode === "labor" ? " — labor-only job: the client marks these ordered" : " — labor + materials job: you handle these"}</p>
        {(detail.material_orders || []).length === 0 && <p className="text-[11px] text-white/35">No material requests yet — the foreman sends them from his page.</p>}
        {(detail.material_orders || []).map((o) => (
          <div key={o.id} className="border border-white/10 rounded-lg bg-background/60 px-3.5 py-2.5 space-y-2" data-testid={`mat-order-${o.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold truncate">{o.item}{o.quantity ? ` · ${o.quantity}` : ""}</p>
              <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${o.status === "ordered" ? "bg-primary/15 text-primary" : "bg-amber-400/15 text-amber-300"}`} data-testid={`mat-status-${o.id}`}>{o.status}</span>
            </div>
            <p className="text-[10px] text-white/40">{o.by} · {fmtTs(o.created_at)}{o.note ? ` · ${o.note}` : ""}</p>
            {(o.eta || o.response_note) && <p className="text-[11px] text-white/60">{o.eta ? `ETA: ${o.eta}` : ""}{o.eta && o.response_note ? " — " : ""}{o.response_note}{o.answered_by ? ` (${o.answered_by})` : ""}</p>}
            {o.status !== "ordered" && (
              <div className="flex gap-1.5">
                <input value={matAnswers[o.id]?.eta || ""} onChange={(e) => setMatAnswers({ ...matAnswers, [o.id]: { ...(matAnswers[o.id] || {}), eta: e.target.value } })} placeholder="ETA — e.g. Fri AM" data-testid={`mat-eta-${o.id}`} className={`${inputCls} !py-2 !text-xs`} />
                <input value={matAnswers[o.id]?.note || ""} onChange={(e) => setMatAnswers({ ...matAnswers, [o.id]: { ...(matAnswers[o.id] || {}), note: e.target.value } })} placeholder="Note" data-testid={`mat-note-${o.id}`} className={`${inputCls} !py-2 !text-xs`} />
                <button onClick={() => answerMaterial(o)} disabled={busy} data-testid={`mat-ordered-${o.id}`} className="shrink-0 border border-primary/60 text-primary text-[10px] font-bold uppercase tracking-widest px-3 rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors duration-200 disabled:opacity-50">Mark ordered</button>
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {view === "reports" && (
      <div className="space-y-2.5" data-testid={`job-reports-${job.id}`}>
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/45 flex items-center gap-2"><ClipboardList size={12} className="text-primary" aria-hidden="true" /> Daily reports from the foreman</p>
        {(detail.daily_reports || []).length === 0 && <p className="text-[11px] text-white/35">No daily reports yet — the foreman submits them from his page.</p>}
        {(detail.daily_reports || []).map((r) => (
          <div key={r.id} className="border border-white/10 rounded-lg bg-background/60 px-3.5 py-3 space-y-1.5" data-testid={`report-${r.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">{fmtDateUS(r.report_date)} <span className="text-white/40 font-normal text-[11px]">· {r.by} · submitted {fmtTs(r.submitted_at)}</span>{r.workers_count > 0 && <span className="text-white/40 font-normal text-[11px]"> · {r.workers_count} on site</span>}</p>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => setReportVisibility(r, !r.client_visible)} data-testid={`report-visible-${r.id}`} title={r.client_visible ? "Visible on the customer's page — tap to hide" : "Hidden from the customer — tap to show"}
                        className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest border px-2.5 py-1.5 rounded-lg transition-colors duration-200 ${r.client_visible ? "border-primary/60 text-primary" : "border-white/20 text-white/40 hover:border-primary hover:text-primary"}`}>
                  <Eye size={11} aria-hidden="true" /> {r.client_visible ? "Client sees" : "Hidden"}
                </button>
                <button onClick={() => downloadReportPdf(r)} data-testid={`report-pdf-${r.id}`} className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest border border-white/20 text-white/55 px-2.5 py-1.5 rounded-lg hover:border-primary hover:text-primary transition-colors duration-200">
                  <FileDown size={11} aria-hidden="true" /> PDF
                </button>
                {detail.client_email && (
                  <button onClick={() => emailReport(r)} data-testid={`report-email-${r.id}`} className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest border border-white/20 text-white/55 px-2.5 py-1.5 rounded-lg hover:border-primary hover:text-primary transition-colors duration-200">
                    <Mail size={11} aria-hidden="true" /> Email to client
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-white/70 leading-relaxed">{r.progress}</p>
            <div className="flex flex-wrap gap-1.5">
              {r.delayed && <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300" data-testid={`report-delayed-${r.id}`}>Delayed{r.delay_reason ? `: ${r.delay_reason}` : ""}</span>}
              {r.material_missing && <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-400/15 text-red-300" data-testid={`report-material-${r.id}`}>Material missing{r.material_note ? `: ${r.material_note}` : ""}</span>}
            </div>
            {r.purchases && <p className="text-[11px] text-white/55" data-testid={`report-purchases-${r.id}`}><span className="text-white/40 font-semibold">Purchases:</span> {r.purchases}</p>}
            {r.concerns && <p className="text-[11px] text-white/50 border-l-2 border-white/20 pl-2.5">{r.concerns}</p>}
            {r.photos?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`report-photos-${r.id}`}>
                {r.photos.map((p) => (
                  <a key={p} href={mediaUrl(p)} target="_blank" rel="noreferrer"><img src={mediaUrl(p)} alt="report" className="w-14 h-14 object-cover rounded-lg border border-white/10" /></a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {view === "messages" && (
      <div className="space-y-2.5" data-testid={`job-clarifications-${job.id}`}>
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/45 flex items-center gap-2"><MessageCircleQuestion size={12} className="text-primary" aria-hidden="true" /> Clarifications</p>
        {(detail.clarifications || []).length === 0 && <p className="text-[11px] text-white/35">No questions yet — you or the foreman can ask the client anything.</p>}
        {[...(detail.clarifications || [])].reverse().map((c) => (
          <div key={c.id} className="border border-white/10 rounded-lg bg-background/60 px-3.5 py-2.5" data-testid={`clarification-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-white/85 flex-1">{c.question}</p>
              <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${c.status === "open" ? "bg-amber-400/15 text-amber-300" : "bg-primary/15 text-primary"}`} data-testid={`clarification-status-${c.id}`}>{c.status}</span>
            </div>
            <p className="text-[10px] text-white/40 mt-1">{c.asked_by} · {fmtTs(c.asked_at)}</p>
            {c.status === "closed" && (
              <p className="text-xs text-white/65 mt-2 border-l-2 border-primary pl-2.5"><span className="text-primary font-semibold">{c.answered_by}:</span> {c.answer} <span className="text-white/35">· {fmtTs(c.answered_at)}</span></p>
            )}
          </div>
        ))}
        <div className="flex gap-2">
          <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask the client something..." data-testid={`clarification-input-${job.id}`} className={inputCls} />
          <button onClick={askQuestion} disabled={busy} data-testid={`clarification-ask-${job.id}`} className="shrink-0 border border-primary text-primary font-bold uppercase tracking-widest text-xs px-4 rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors duration-200 disabled:opacity-50">Ask</button>
        </div>
      </div>
      )}

      {view === "activity" && (
      <div data-testid={`job-activity-${job.id}`}>
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/45 mb-2">Activity log</p>
        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
          {[...(detail.activity || [])].reverse().map((a, i) => (
            <p key={i} className="text-[11px] text-white/55 flex gap-2">
              <Check size={12} className="text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <span><span className="text-white/35">{fmtTs(a.ts)}</span> — {a.text} <span className="text-white/35">({a.by})</span></span>
            </p>
          ))}
        </div>
      </div>
      )}

      {viewReceipt && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center p-4" data-testid="receipt-viewer" onClick={() => setViewReceipt(null)}>
          <div className="max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-xs text-white/75 font-semibold truncate">{money2(viewReceipt.amount)} · {viewReceipt.note || "receipt"} · by {viewReceipt.by} · {fmtTs(viewReceipt.ts)}</p>
              <button onClick={() => setViewReceipt(null)} aria-label="Close receipt viewer" data-testid="receipt-viewer-close" className="text-white/60 hover:text-primary p-1.5 shrink-0 transition-colors duration-200"><X size={18} /></button>
            </div>
            <img src={mediaUrl(viewReceipt.receipt_path)} alt={viewReceipt.note || "Receipt photo"} className="w-full max-h-[75vh] object-contain rounded-lg border border-white/15 bg-black" data-testid="receipt-viewer-img" />
            <p className="text-[10px] text-white/40 mt-2 text-center">Tap the checkbox on this receipt's line to add it at cost to the invoice</p>
          </div>
        </div>
      )}
    </div>
  );
};

export const JobsTab = () => {
  const [jobs, setJobs] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/jobs").then(({ data }) => setJobs(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-jobs-tab" className="space-y-4">
      <h1 className="font-display text-4xl tracking-wide">OPEN PROJECTS</h1>
      <p className="text-white/40 text-xs">A proposal becomes an open project the moment the client approves and signs. Crew logs hours at <span className="text-primary font-bold">/time</span> — crew and payroll live in the General tab.</p>

      {jobs.length === 0 && (
        <div className="border border-dashed border-white/15 rounded-2xl py-14 flex flex-col items-center gap-3 text-white/40" data-testid="jobs-empty">
          <Briefcase size={28} aria-hidden="true" />
          <p className="text-sm">No open projects yet — approve a signed proposal and it appears here.</p>
        </div>
      )}

      <div className="space-y-3">
        {jobs.map((j) => (
          <div key={j.id} className={`border rounded-2xl bg-card overflow-hidden transition-colors duration-300 ${openId === j.id ? "border-primary/40" : "border-white/10"}`} data-testid={`job-card-${j.id}`}>
            <button onClick={() => setOpenId(openId === j.id ? null : j.id)} data-testid={`job-toggle-${j.id}`} className="w-full p-5 text-left">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-bold truncate" data-testid={`job-client-${j.id}`}>{j.client_name}</p>
                  <p className="text-white/50 text-xs truncate">{j.title}</p>
                  {j.address && (
                    <p className="text-white/40 text-[11px] mt-1 flex items-center gap-1 truncate">
                      <MapPin size={11} aria-hidden="true" /> {j.address}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display text-2xl text-primary" data-testid={`job-total-${j.id}`}>{money(j.total_price)}</p>
                  <p className="text-[10px] text-white/40 uppercase tracking-widest">total price</p>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-white/45 mb-1.5">
                  <span className={`${j.status !== "open" ? "text-amber-300" : ""}`}>{j.status.replace("_", " ")}</span>
                  <span data-testid={`job-pct-${j.id}`}>{j.completion_pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${j.completion_pct}%` }} />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] text-white/40">
                <span>{fmtDateUS(j.created_at)} · {j.activity_count} log entr{j.activity_count === 1 ? "y" : "ies"}</span>
                <ChevronDown size={15} className={`transition-transform duration-300 ${openId === j.id ? "rotate-180 text-primary" : ""}`} aria-hidden="true" />
              </div>
            </button>
            {openId === j.id && <JobDetail job={j} onChanged={load} />}
          </div>
        ))}
      </div>
    </div>
  );
};
